import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "mahra_recently_viewed";
const MAX_ITEMS = 6;

export function addToRecentlyViewed(outfit) {
  if (!outfit?.id) return;
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    const filtered = stored.filter(o => o.id !== outfit.id);
    filtered.unshift({
      id: outfit.id,
      name: outfit.name,
      price_per_day: outfit.price_per_day,
      price_4_day: outfit.price_4_day,
      image: outfit.images?.[0] || "",
      category: outfit.category || "",
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered.slice(0, MAX_ITEMS)));
  } catch {}
}

export default function RecentlyViewed({ currentOutfitId }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    async function validate() {
      try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const filtered = stored.filter(o => o.id !== currentOutfitId);
        if (filtered.length === 0) return;
        // Validate against current DB — fetch all outfit IDs
        const res = await axios.get(`${API}/outfits`);
        const visibleIds = new Set((res.data || []).filter(o => o.available !== false).map(o => o.id));
        const validated = filtered.filter(o => visibleIds.has(o.id));
        // Update localStorage to remove stale items
        if (validated.length !== stored.length) {
          const allValid = stored.filter(o => visibleIds.has(o.id));
          localStorage.setItem(STORAGE_KEY, JSON.stringify(allValid.slice(0, MAX_ITEMS)));
        }
        setItems(validated.slice(0, MAX_ITEMS));
      } catch {
        // Fallback: show stored items without validation
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        setItems(stored.filter(o => o.id !== currentOutfitId).slice(0, MAX_ITEMS));
      }
    }
    validate();
  }, [currentOutfitId]);

  if (items.length === 0) return null;

  return (
    <section className="mt-10 lg:mt-16" data-testid="recently-viewed-section">
      <div className="flex items-center gap-3 mb-6">
        <Clock className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
        <h2 className="text-lg lg:text-xl text-[#282828] font-light">Recently Viewed</h2>
      </div>
      <div className="flex gap-3 lg:gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/outfit/${item.id}`}
            className="flex-shrink-0 w-[140px] sm:w-[160px] lg:w-[180px] group"
            data-testid={`recent-${item.id}`}
          >
            <div className="relative overflow-hidden bg-[#f7f5f4]" style={{ height: 0, paddingBottom: "133%" }}>
              <img
                src={item.image}
                alt={item.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
            <div className="pt-2">
              <p className="text-[11px] text-[#282828] truncate">{item.name}</p>
              <p className="text-[10px] text-[#676664] font-light mt-0.5">From ${item.price_4_day || item.price_per_day || 0} / rental</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
